import { useState, useCallback, useMemo } from 'react';
import { useSupabaseQuery } from './useSupabaseQuery';
import type { Database } from '../lib/database.types';

export interface OpComp {
  key: string;
  fuelBrand: string | null;
  storeBrand: string | null;
  address: string | null;
  city: string | null;
  county: string | null;
  state: string | null;
  milePop: number | null;
  mileIncome: number | null;
  popGrowth: number | null;
  trafficCounts: number | null;
  access: number | null;
  signalized: string | null;
  siteSize: number | null;
  storeBldgSize: number | null;
  fuelingCanopySize: string | null;
  carWashSize: string | null;
  otherSize: string | null;
  yearBuilt: number | null;
  condition: string | null;
  numOfDisp: number | null;
  ustCapacity: string | null;
  diesel: string | null;
  fuelVol: number | null;
  cStoreSales: string | null;
  cwSales: string | null;
  salesPerSF: string | null;
  fuelMargin: string | null;
  cStoreMargin: string | null;
  grossProfit: string | null;
  opEx: string | null;
  opExGP: string | null;
  ebitda: number | null;
  year: number | null;
  notes: string | null;
}

export interface OpCompFilterState {
  fuelBrand: string;
  city: string;
  state: string;
  minSiteSize: number;
  maxSiteSize: number;
  minFuelVol: number;
  maxFuelVol: number;
  minYear: number;
  maxYear: number;
  similarityRange: number;
}

export interface OpCompComparisonMetrics {
  siteSizeDiff: number;
  fuelVolDiff: number;
  storeSizeDiff: number;
  overallSimilarity: number;
}

type OpCompRow = Database['public']['Tables']['OpComps']['Row'];

export function useOpComps() {
  const [referenceProperty, setReferenceProperty] = useState<OpComp | null>(null);
  const [filters, setFilters] = useState<OpCompFilterState>({
    fuelBrand: '',
    city: '',
    state: '',
    minSiteSize: 0,
    maxSiteSize: 0,
    minFuelVol: 0,
    maxFuelVol: 0,
    minYear: 0,
    maxYear: 0,
    similarityRange: 0.2,
  });

  const { data: rawOpComps, error, loading } = useSupabaseQuery<'OpComps'>('OpComps', {
    select: '*', // Select all columns from the OpComps table
  });

  // Convert raw data from database format to our application format
  const opComps = useMemo(() => {
    if (!rawOpComps) return [];
    
    return rawOpComps.map((comp: OpCompRow) => ({
      key: comp.Key,
      fuelBrand: comp.Fuel_Brand,
      storeBrand: comp.Store_Brand,
      address: comp.Address,
      city: comp.City,
      county: comp.County,
      state: comp.State,
      milePop: comp["1-Mile_Pop"],
      mileIncome: comp["1-Mile_HH_Income"],
      popGrowth: comp.Pop_Growth,
      trafficCounts: comp.ADT_Traffic_Counts,
      access: comp.Access,
      signalized: comp.Signalized,
      siteSize: comp.Site_Size,
      storeBldgSize: comp.Store_Bldg_Size,
      fuelingCanopySize: comp.Fueling_Canopy_Size,
      carWashSize: comp.Car_Wash_Size,
      otherSize: comp.Other_Size,
      yearBuilt: comp.Year_Built,
      condition: comp.Condition,
      numOfDisp: comp.No_of_Disp,
      ustCapacity: comp.UST_Capacity,
      diesel: comp.Diesel,
      fuelVol: comp.Fuel_Vol,
      cStoreSales: comp.C_Store_Sales,
      cwSales: comp.CW_Sales,
      salesPerSF: comp.Sales_per_SF,
      fuelMargin: comp.Fuel_Margin,
      cStoreMargin: comp.C_Store_Margin,
      grossProfit: comp.Gross_Profit,
      opEx: comp.OpEx,
      opExGP: comp.OpEx_GP,
      ebitda: comp.EBITDA,
      year: comp.Year,
      notes: comp.Notes
    }));
  }, [rawOpComps]);

  // Calculate similarity between properties for comparative analysis
  const calculateSimilarity = useCallback((property: OpComp): OpCompComparisonMetrics => {
    if (!referenceProperty) return {
      siteSizeDiff: 0,
      fuelVolDiff: 0,
      storeSizeDiff: 0,
      overallSimilarity: 0
    };

    const siteSizeDiff = Math.abs((property.siteSize || 0) - (referenceProperty.siteSize || 0)) / (referenceProperty.siteSize || 1);
    const fuelVolDiff = Math.abs((property.fuelVol || 0) - (referenceProperty.fuelVol || 0)) / (referenceProperty.fuelVol || 1);
    const storeSizeDiff = Math.abs((property.storeBldgSize || 0) - (referenceProperty.storeBldgSize || 0)) / (referenceProperty.storeBldgSize || 1);
    
    // Calculate overall similarity score (1 = identical, 0 = completely different)
    const overallSimilarity = 1 - ((siteSizeDiff + fuelVolDiff + storeSizeDiff) / 3);

    return {
      siteSizeDiff,
      fuelVolDiff,
      storeSizeDiff,
      overallSimilarity
    };
  }, [referenceProperty]);

  // Apply filters to the properties
  const filteredOpComps = useMemo(() => {
    return opComps.filter(property => {
      // Skip the reference property itself
      if (property.key === referenceProperty?.key) return false;
      
      // Apply similarity filter if there's a reference property
      if (referenceProperty) {
        const similarity = calculateSimilarity(property);
        if (similarity.overallSimilarity < (1 - filters.similarityRange)) return false;
      }
      
      // Apply other filters
      if (filters.fuelBrand && property.fuelBrand !== filters.fuelBrand) return false;
      if (filters.city && property.city !== filters.city) return false;
      if (filters.state && property.state !== filters.state) return false;
      if (filters.minSiteSize && (property.siteSize || 0) < filters.minSiteSize) return false;
      if (filters.maxSiteSize && (property.siteSize || 0) > filters.maxSiteSize) return false;
      if (filters.minFuelVol && (property.fuelVol || 0) < filters.minFuelVol) return false;
      if (filters.maxFuelVol && (property.fuelVol || 0) > filters.maxFuelVol) return false;
      if (filters.minYear && (property.year || 0) < filters.minYear) return false;
      if (filters.maxYear && (property.year || 0) > filters.maxYear) return false;
      
      return true;
    });
  }, [opComps, referenceProperty, filters, calculateSimilarity]);

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const fuelBrands = new Set<string>();
    const cities = new Set<string>();
    const states = new Set<string>();
    
    opComps.forEach(comp => {
      if (comp.fuelBrand) fuelBrands.add(comp.fuelBrand);
      if (comp.city) cities.add(comp.city);
      if (comp.state) states.add(comp.state);
    });
    
    return {
      fuelBrands: Array.from(fuelBrands).sort(),
      cities: Array.from(cities).sort(),
      states: Array.from(states).sort(),
    };
  }, [opComps]);

  return {
    opComps,
    filteredOpComps,
    referenceProperty,
    setReferenceProperty,
    filters,
    setFilters,
    calculateSimilarity,
    filterOptions,
    error,
    loading
  };
}
